import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ReactHighchartsOfficial from 'highcharts-react-official';
import HighchartsGantt from 'highcharts/highcharts-gantt';
import moment from 'moment';
import { getEquipmentGroupList } from '../../actions/equipment-group';
import { DISPLAY_DATETIME_FORMAT2 } from '../../constants';
import { toDisplayDateTimeString } from '../../helpers/moment-ex';
import '../../../assets/scss/gantt-chart.scss';
import FbPost from '../widgets/FbPost';
import { getComponentAsBlob, getCloneGanttChartElement } from '../../helpers/fileConverter';
import { getDateRange } from '../../helpers/common';

const GanttChart = (props) => {
    const { isRequesting, equipmentGroupList, actions, match, search, farmName } = props;
    const defaultMaxDisplay = 12;
    const chartTitle = 'Manage Equipment Groups';

    const colors = ["#19B5FE", "#41ea38", "#e74c3c", "#E08A1E", "#F62459",
        "#FF20A5", "#9b59b6", "#CA2C68", "#2ecc71", "#3498db",
        "#f1c40f", "#8e44ad", "#c0392b", "#4fe2d6", "#ff926b",
        "#5e2c0b", "#D35400", "#A17917", "#1abc9c", "#34495e"];

    const getOptions = (opt) => ({
        chart: {
            type: "gantt",
            plotBackgroundColor: "#f3f3f3",
            plotBorderColor: "#b4b4b4",
            plotBorderWidth: 1,
            zoomType: 'xy',
            marginRight: "41",
            marginTop: "110",
        },
        credits: {
            enabled: false
        },
        exporting: {
            enabled: true,
            buttons: {
                contextButton: {
                    symbolSize: 16,
                    symbolY: 15,
                    menuItems: ["downloadPNG", "downloadJPEG", "downloadPDF", "downloadSVG"]
                }
            }
        },
        navigator: {
            enabled: true,
            liveRedraw: true,
            margin: 25,
            maskFill: "#ffc8327e",
            outlineColor: "#ffd65b",
            outlineWidth: 2,
            series: {
                type: 'gantt',
                pointPlacement: 0.5,
                pointPadding: 0.25,
                dataLabels: [{
                    enabled: true
                }]
            },
            yAxis: {
                reversed: true,
                categories: []
            }
        },
        rangeSelector: {
            enabled: true,
            buttons: [{
                type: 'month',
                count: 1,
                text: '1m'
            }, {
                type: 'month',
                count: 3,
                text: '3m'
            }, {
                type: 'month',
                count: 6,
                text: '6m'
            }, {
                type: 'year',
                count: 1,
                text: '1y'
            }, {
                type: 'ytd',
                text: 'YTD'
            }, {
                type: 'all',
                text: 'All'
            }],
            selected: 6,
            inputEnabled: false,
            floating: true,
            x: -315,
            y: -50
        },
        scrollbar: {
            enabled: true,
            liveRedraw: false
        },
        series: [{
            name: 'Project 1',
            data: opt.data,
            groupPadding: 0.2,
            cursor: "pointer",
            dataLabels: {
                enabled: true,
                align: "left",
                format: '{point.subName}',
            },
        }],
        title: {
            text: `<div class="title">${chartTitle}</div>`,
            useHTML: true,
            align: "left",
            floating: true
        },
        tooltip: {
            outside: true,
            backgroundColor: "#fbfbfb",
            borderRadius: 3,
            padding: 10,
            useHTML: true,
            formatter() {
                return `<div class="gantt-tool-tip">
                <div class="title">${this.point.subName}</div>
                <p class="sub-title">${this.point.name}</p>
                <div ><span class="label">Start </span><span >: ${moment.utc(this.point.start).format(DISPLAY_DATETIME_FORMAT2)}</span></div>
                <div ><span class="label">End </span><span class=${this.point.alive ? "alive" : "name"}>: ${this.point.alive ? "Now" : moment.utc(this.point.end).format(DISPLAY_DATETIME_FORMAT2)}</span></div>
                </div>`
            }
        },
        xAxis: [{
            type: "datetime",
            tickInterval: 1000 * 60 * 60 * 24 * 31,
            labels: {
                format: '{value:%B}'
            },
        }, {
            type: "datetime",
            tickInterval: 1000 * 60 * 60 * 24 * 365,
            labels: {
                format: '{value:%Y}'
            }
        }],
        yAxis: {
            min: 0,
            max: opt.maxDisplay,
            type: 'category',
            margin: -1,
            grid: {
                enabled: true,
                borderWidth: 1,
                columns: [{
                    labels: {
                        useHTML: true,
                        format: `<div class="list-name">
                        <img src="{point.img}" class="image"/>
                        <span class="name {point.className}">{point.name}</span>
                        <div class="date {point.className}">{point.displayStartDate}
                        <i class="fa fa-long-arrow-right date-btw" aria-hidden="true"></i>
                        {point.displayEndDate}</div>
                        </div>`
                    },
                    className: "grid-column-item",
                    scrollbar: {
                        enabled: opt.data.length > defaultMaxDisplay ? true : false
                    }
                }]
            }
        },
    });

    const [options, setOptions] = useState(getOptions({ maxDisplay: defaultMaxDisplay, data: [] }));
    const ganttChartRef = useRef();

    useEffect(() => {
        const { farmId } = match.params;
        actions.getEquipmentGroupList({ farmId, crew: 1, device: 1 });
    }, [])

    useEffect(() => {
        const { farmId } = match.params;
        actions.getEquipmentGroupList({ farmId, crew: 1, device: 1 });
    }, [search.range, search.customRange, search.custom])

    useEffect(() => {
        let data = [];
        const colorsForCrew = {};
        const equipmentCategory = {
            9: { sortOrder: 1 },
            34: { sortOrder: 2 },
            31: { sortOrder: 3 },
            7: { sortOrder: 4 },
            1: { sortOrder: 5 },
            2: { sortOrder: 6 },
            3: { sortOrder: 7 },
            4: { sortOrder: 8 },
            5: { sortOrder: 9 },
            8: { sortOrder: 10 },
            11: { sortOrder: 11 },
            29: { sortOrder: 12 },
            10: { sortOrder: 20 },
            28: { sortOrder: 21 },
            43: { sortOrder: 22 },
            12: { sortOrder: 23 },
            13: { sortOrder: 24 },
            6: { sortOrder: 25 }
        };

        const egList = equipmentGroupList
            .filter(e => e.img.ic && e.img.ic.length > 0)
            .map(e => {
                const sortOrder = equipmentCategory.hasOwnProperty(e.primary_category_id) && equipmentCategory[e.primary_category_id].sortOrder ?
                    equipmentCategory[e.primary_category_id].sortOrder : 99;
                return { ...e, sortOrder };
            }).sort((a, b) => (a.sortOrder - b.sortOrder));

        egList.forEach((item, i) => {
            const series = {
                name: item.equipment_group_name,
                startDate: moment.utc(item.start_date).format(DISPLAY_DATETIME_FORMAT2),
                endDate: moment.utc(item.end_date).format(DISPLAY_DATETIME_FORMAT2),
                displayStartDate: toDisplayDateTimeString(item.start_date, (moment.utc(item.start_date).year() === moment.utc().year())),
                displayEndDate: item.alive ? "Now" : toDisplayDateTimeString(item.end_date, (moment.utc(item.end_date).year() === moment.utc().year())),
                img: item.img.ic,
                alive: item.alive,
                className: item.alive ? "alive" : "",
                y: i
            }
            if (item.crews.length === 0) data.push(series);
            else {
                item.crews.forEach((crew) => {
                    let color;
                    if (colorsForCrew.hasOwnProperty(crew.crew_uuid)) color = colorsForCrew[crew.crew_uuid];
                    else {
                        color = colors[Object.keys(colorsForCrew).length % 20];
                        colorsForCrew[crew.crew_uuid] = color;
                    };
                    data.push({
                        ...series,
                        start: moment.utc(crew.start_date).valueOf(),
                        end: crew.alive ? moment.utc().valueOf() : moment.utc(crew.end_date).valueOf(),
                        alive: crew.alive,
                        color: color,
                        borderWidth: 1,
                        borderRadius: 2,
                        borderColor: "#383030",
                        subName: crew.name
                    });
                });
            }
        });

        setOptions(getOptions({
            data,
            maxDisplay: egList.length > defaultMaxDisplay ? defaultMaxDisplay : egList.length - 1
        }));

    }, [equipmentGroupList]);

    const afterChartCreated = chart => {
        const H = HighchartsGantt;
        H.addEvent(chart.container, document.onmousewheel === undefined ? 'DOMMouseScroll' : 'mousewheel', function (event) {
            const seriesDataCount = chart.userOptions.series.length > 0 ? chart.userOptions.series[0].data.length : 0;
            if (seriesDataCount <= defaultMaxDisplay + 1) return;
            var delta, extr, step, axis = chart.yAxis[0];
            const e = chart.pointer.normalize(event);

            delta = e.detail || -(e.wheelDelta / 120);
            delta = delta < 0 ? -1 : 1;

            if (chart.isInsidePlot(e.chartX - chart.plotLeft, e.chartY - chart.plotTop)) {
                const maxDisplay = options.yAxis.max;
                extr = axis.getExtremes();
                step = 1 / 2 * delta;
                let min = extr.min + step;
                let max = extr.max + step;
                min = min <= 0 ? 0 : max >= extr.dataMax ? extr.dataMax - maxDisplay : min;
                max = min <= 0 ? maxDisplay : max >= extr.dataMax ? extr.dataMax : max;
                axis.setExtremes(min, max, true, false);
            }
            return false;
        });
    }
    const dateRange = getDateRange({ ...search });
    const postDescription = `${dateRange.name}${dateRange.range ? ` - ${dateRange.range}` : ''}`;
    const getImageUrl = async () => {
        const clonedChartElement = getCloneGanttChartElement(ganttChartRef.current);
        return await getComponentAsBlob(clonedChartElement);
    }

    return (<div ref={ganttChartRef} className="gantt-chart">
        {!isRequesting && (
            <>
                <div className="gantt-chart-share">
                    <FbPost
                        chartName={chartTitle}
                        description={postDescription}
                        getImageUrl={getImageUrl}
                        isLoading={isRequesting}
                        title={farmName}
                    />
                </div>
                <ReactHighchartsOfficial highcharts={HighchartsGantt} constructorType={"ganttChart"}
                    options={options} callback={afterChartCreated} />
            </>
        )}
        {isRequesting && <div className="gantt-chart-loading">Loading...</div>}
        <div className="image-watermark d-none">Powered by acuitusag</div>
    </div>);
}

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        getEquipmentGroupList
    }, dispatch)
});

const mapStateToProps = state => ({
    isRequesting: state.equipmentGroup.isRequesting,
    farmName: state.farm.currentFarm.farm_name,
    equipmentGroupList: state.equipmentGroup.equipmentGroupList,
    search: {
        range: state.analyze.searchParams.range,
        customRange: state.analyze.searchParams.customRange,
        custom: state.analyze.searchParams.custom
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(GanttChart);